import { generatePath } from 'react-router';

import { AvatarInfo } from '@components/avatar-info';
import { EmptyState } from '@components/empty-state';
import { UploadedFile } from '@components/file-field';
import { HideContainer } from '@components/hide-container';
import { IconButton } from '@components/icon-button';
import { IconAttributeJourney } from '@components/icons/attribute-journey';
import { IconAttributeTraveller } from '@components/icons/attribute-traveller';
import { IconEdit } from '@components/icons/edit';
import { IconInfoSimple } from '@components/icons/info-simple';
import { IconTextFormatLink } from '@components/icons/text-format-link';
import { LabelAvatarNameGroup } from '@components/label-avatar-name-group';
import { LabelTextGroup } from '@components/label-text-group';
import { Spinner } from '@components/loading-spinner';
import { usePreviewFileModal } from '@components/modals/preview-file-modal';
import { Sidebar, useSidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { TextStatusIndicator, TextStatusIndicatorProps } from '@components/text-status-indicator';
import { useClipboard } from '@hooks/use-clipboard';
import { useScopedTranslate } from '@locale/translate';
import { abbreviateDate } from '@utils/date';
import { formatNumber, hasNumericValue } from '@utils/numbers';

import { useCompanyTravellerAttributeTemplates } from 'api/attributes/hooks';
import { AttributeType } from 'api/attributes/requests';
import { useJourney } from 'api/journeys/hooks';
import { useJourneyTraveller } from 'api/traveller-journeys/hooks';
import { TravellerAttribute } from 'api/types';
import { useUsers } from 'api/users/hooks';
import { usePermissions } from 'contexts/permissions-context';
import { routePaths } from 'ui/authenticated/navigation';
import { useFeature } from 'ui/hooks';

import { useCompanyTravellerToJourneySidebar } from './company-traveller-to-journey';

import { ViewCompanyTravellerAttributes } from '../company-travellers/sidebars/profile';

export const useTravellerDetailsSidebar = () =>
  useSidebar(TravellerDetailsSidebar, {
    variant: 'REGULAR',
  });

export const TravellerDetailsSidebar = ({
  travellerId,
  journeyId,
}: {
  travellerId: string;
  journeyId: string;
}) => {
  const { t } = useScopedTranslate(locale);
  const { canEditJourneyTravellerData } = usePermissions();
  const showEditTravellerSidebar = useCompanyTravellerToJourneySidebar();
  const { isEnabled: hasTimeSavedEnabled } = useFeature('FEATURE_TIME_SAVED');
  const { onCopy } = useClipboard();

  const travellerQuery = useJourneyTraveller(travellerId);
  const companyTravellerAttributeTemplatesQuery = useCompanyTravellerAttributeTemplates();
  const companyTravellerAttributeTemplates = companyTravellerAttributeTemplatesQuery.data ?? [];
  const journeyQuery = useJourney(journeyId);

  if (
    companyTravellerAttributeTemplatesQuery.isLoading ||
    journeyQuery.isLoading ||
    !travellerQuery.isSuccess
  ) {
    return <Spinner />;
  }

  const journeyAttributes = journeyQuery.data?.attributes ?? [];
  const traveller = travellerQuery.data;

  const copyUrl = () => {
    const path = generatePath(routePaths.traveller, {
      journeyId,
      travellerId,
      workspaceId: journeyQuery.data?.workspace ?? '',
      '*': '',
    });
    onCopy(window.location.origin + path, t('travellercopy'));
  };

  const filteredCoreAttributes = companyTravellerAttributeTemplates?.filter(
    (coreAttribute) => coreAttribute.isActive
  );

  const hasJourneyFields = journeyAttributes.length > 0;

  return (
    <Sidebar>
      <Sidebar.SimpleHeader
        title={t('travellerDetails')}
        icon={IconInfoSimple}
        iconColor="elementPurpleInvert"
      />
      <Sidebar.Content>
        <Stack space={4}>
          <AvatarInfo
            title={traveller.name}
            subtitles={[traveller.email, traveller.phone]}
            src={traveller.picture}
          />
          <div>
            {traveller.dateCreated && (
              <TravellerStatusDate
                title={t('created')}
                variant="info"
                date={traveller.dateCreated}
              />
            )}
            {traveller.dateStarted && (
              <TravellerStatusDate
                title={t('journeyStarted')}
                variant="pending"
                date={traveller.dateStarted}
              />
            )}
            {traveller.dateCompleted && (
              <TravellerStatusDate
                title={t('journeyCompleted')}
                variant="success"
                date={traveller.dateCompleted}
              />
            )}
          </div>
          <Sidebar.Separator />
          <HideContainer
            title={t('travellerAttributes')}
            caps
            defaultClosed
            icon={<IconAttributeTraveller />}
          >
            <Stack space={4}>
              <div>
                <Text text={t('core.fullName')} font="semibold14" />
                <Text font="regular14" text={traveller.name} />
              </div>
              <div>
                <Text text={t('core.email')} font="semibold14" />
                <Text font="regular14" text={traveller.email} />
              </div>
              <div>
                <Text text={t('core.phone')} font="semibold14" />
                <Text
                  font="regular14"
                  text={Boolean(traveller.phone) ? traveller.phone : t('nophone')}
                  color={Boolean(traveller.phone) ? 'textPrimary' : 'textHelper'}
                />
              </div>
              {filteredCoreAttributes?.map((attribute) => {
                const travellerAttribute = traveller?.companyTravellerAttributes.find(
                  (companyAttribute) => companyAttribute.attributeShortId === attribute.shortId
                );
                return (
                  attribute.isActive && (
                    <ViewCompanyTravellerAttributes
                      key={attribute.shortId}
                      attributeTemplate={attribute}
                      travellerAttribute={travellerAttribute}
                    />
                  )
                );
              })}
            </Stack>
          </HideContainer>
          <Sidebar.Separator />
          <HideContainer
            title={t('journeyAttributes')}
            caps
            icon={<IconAttributeJourney />}
            defaultClosed={!hasJourneyFields}
          >
            {hasJourneyFields ? (
              <Stack space={2}>
                {journeyAttributes.map((attribute) => {
                  const travellerAttribute = traveller.journeyAttributes.find(
                    (travellerAttribute) =>
                      travellerAttribute.attributeShortId === attribute.shortId
                  );
                  return (
                    attribute.isActive && (
                      <ViewTravellerJourneyAttributes
                        key={travellerAttribute?.attributeShortId ?? attribute.shortId}
                        attributeTemplate={attribute}
                        travellerJourneyAttribute={travellerAttribute}
                      />
                    )
                  );
                })}
              </Stack>
            ) : (
              <EmptyState
                skipMargin
                header={t('noJourneyFields')}
                content={t('noFieldsDescription')}
                contentColor="textSecondary"
                headerColor="textPrimary"
                variant="small"
                icon={<IconAttributeJourney size={40} />}
              />
            )}
          </HideContainer>
          <Sidebar.Separator />
          {traveller.source && (
            <LabelTextGroup
              label={t('source')}
              text={[traveller.source, traveller.sourceDescription]}
            />
          )}
          {hasTimeSavedEnabled && traveller.timeSaved && (
            <LabelTextGroup
              label={t('timeSaved')}
              text={t('time', {
                days: traveller.timeSaved.days,
                hours: traveller.timeSaved.hours,
                minutes: traveller.timeSaved.minutes,
              })}
            />
          )}
        </Stack>
      </Sidebar.Content>
      <Sidebar.Footer>
        <Stack orientation="horizontal">
          {canEditJourneyTravellerData && (
            <IconButton
              icon={<IconEdit />}
              variant="ghost-purple"
              tooltip={t('editTraveller')}
              aria-label={t('editTraveller')}
              onClick={() =>
                showEditTravellerSidebar({
                  journeyShortId: traveller.journey.shortId,
                  journeyTravellerId: traveller.shortId,
                })
              }
            />
          )}
          <IconButton
            icon={<IconTextFormatLink />}
            variant="ghost-purple"
            tooltip={t('copyHelpText')}
            aria-label={t('copyHelpText')}
            onClick={copyUrl}
          />
        </Stack>
      </Sidebar.Footer>
    </Sidebar>
  );
};

const ViewTravellerJourneyAttributes = ({
  attributeTemplate,
  travellerJourneyAttribute,
}: {
  attributeTemplate: AttributeType;
  travellerJourneyAttribute?: TravellerAttribute;
}) => {
  const { t } = useScopedTranslate();
  const showFilePreviewModal = usePreviewFileModal();
  const userQuery = useUsers();
  if (userQuery.isLoading) return <Spinner />;

  const usersData = userQuery.data;
  const attributeValue = travellerJourneyAttribute?.value;
  const attrMultiValues = travellerJourneyAttribute?.multiOptionValues ?? [];
  const attrFiles = travellerJourneyAttribute?.files ?? [];
  const isEmptyValue =
    attributeValue === null ||
    attributeValue === undefined ||
    (typeof attributeValue === 'string' && attributeValue.trim() === '');

  const isAttributeNotSet =
    !travellerJourneyAttribute ||
    (isEmptyValue && attrFiles.length === 0 && attrMultiValues.length === 0);

  const renderAttribute = (travellerJourneyAttribute: TravellerAttribute) => {
    switch (travellerJourneyAttribute.attributeType) {
      case 'date':
        const date = abbreviateDate(travellerJourneyAttribute.value, 'en');
        return (
          <Text
            font="regular14"
            text={date ?? t('core.not_set')}
            color={date ? 'textPrimary' : 'textHelper'}
          />
        );
      case 'number':
      case 'decimal':
        const hasValue = hasNumericValue(travellerJourneyAttribute.value);
        const formattedNumber = hasValue
          ? formatNumber(travellerJourneyAttribute.value)
          : undefined;
        return (
          <Text
            font="regular14"
            text={formattedNumber ?? t('core.not_set')}
            color={hasValue ? 'textPrimary' : 'textHelper'}
          />
        );
      case 'user':
        const user = usersData?.find((u) => u.shortId === travellerJourneyAttribute.value.shortId);
        return (
          <LabelAvatarNameGroup
            key={travellerJourneyAttribute.attributeShortId}
            label={travellerJourneyAttribute.attributeTitle}
            hideLabel
            name={user?.name}
            image={user?.picture}
          />
        );
      case 'single_select':
        return (
          <Text
            font="regular14"
            text={travellerJourneyAttribute.value.label ?? t('core.not_set')}
            color={travellerJourneyAttribute.value.label ? 'textPrimary' : 'textHelper'}
          />
        );
      case 'multi_select':
        return (
          <>
            {travellerJourneyAttribute.multiOptionValues.map((option) => (
              <Text
                key={option.value}
                font="regular14"
                text={option.label}
                color={option.label ? 'textPrimary' : 'textHelper'}
              />
            ))}
          </>
        );
      case 'file':
        const file = travellerJourneyAttribute.files[0];
        return (
          file && (
            <UploadedFile
              name={file.name}
              path={file.src}
              onPreview={() => {
                showFilePreviewModal({ formFile: file, filesList: [file] });
              }}
            />
          )
        );
      case 'file_multi':
        const files = travellerJourneyAttribute.files;
        return (
          <Stack space={2}>
            {files?.map((file, index) => (
              <UploadedFile
                key={index}
                name={file.name}
                path={file.src}
                onPreview={() => {
                  showFilePreviewModal({
                    formFile: file,
                    filesList: files,
                  });
                }}
              />
            ))}
          </Stack>
        );
      default:
        return <Text font="regular14" text={travellerJourneyAttribute.value} />;
    }
  };

  const content = isAttributeNotSet ? (
    <Text font="regular14" text={t('core.not_set')} color="textHelper" />
  ) : (
    renderAttribute(travellerJourneyAttribute)
  );

  return (
    <div data-testid="traveller-attribute-row">
      <Text
        font="semibold14"
        text={travellerJourneyAttribute?.attributeTitle ?? attributeTemplate.title}
      />
      {content}
    </div>
  );
};

const TravellerStatusDate = ({
  variant,
  title,
  date,
}: {
  variant: TextStatusIndicatorProps['variant'];
  title: string;
  date: string;
}) => {
  return (
    <Stack orientation="horizontal" space={2} align="center" data-testid="traveller-status-date">
      <TextStatusIndicator text={title} variant={variant} />
      <Text color="textHelper" font="regular14" text="•" />
      <Text color="textHelper" font="regular14" text={abbreviateDate(date, 'en', 'datetime')} />
    </Stack>
  );
};

const locale = {
  en: {
    travellerDetails: 'Traveller details',
    firstWorkday: 'First workday',
    supervisor: 'Supervisor',
    source: 'Source',
    created: 'Created',
    journeyStarted: 'Journey started',
    journeyCompleted: 'Journey completed',
    copyHelpText: 'Copy traveller link',
    editTraveller: 'Edit traveller',
    travellercopy: 'Traveller link copied to clipboard',
    journeyAttributes: 'Journey fields',
    travellerAttributes: 'Traveller fields',
    notSet: 'not set',
    noJourneyFields: 'No journey fields',
    noFieldsDescription: 'This journey has no fields configured',
    timeSaved: 'Time saved',
    time: '{days} days, {hours} hours, {minutes} minutes',
    nophone: 'No number added',
  },
};
